import { cx } from 'class-variance-authority'

import { CustomCellRendererProps } from 'ag-grid-react'
import { useTranslations } from 'next-intl'
import {
  pendingRefDisplayClassName,
  resolveRefDisplayEntries,
} from '@/components/Grid/utils/refDataDisplay'
import type { GridContext } from '@/types/grid'

export function ChipsCellRenderer(
  params: CustomCellRendererProps<
    Record<string, unknown>,
    unknown,
    GridContext
  >,
) {
  const t = useTranslations('Property')
  const { value, colDef } = params
  const context = params.context as GridContext | undefined
  const field = typeof colDef?.field === 'string' ? colDef.field : undefined
  if (!value) return null
  if (!field) return null

  const editorProperty = colDef?.cellEditorParams?.property as
    | { type?: unknown }
    | undefined
  const editorFieldType =
    typeof editorProperty?.type === 'string' ? editorProperty.type : undefined
  const supportedFieldType = context?.supportedKeys?.find(
    (supportedKey) => supportedKey.key === field,
  )?.type
  const fieldType =
    editorFieldType ??
    (typeof supportedFieldType === 'string' ? supportedFieldType : undefined)

  const chips = resolveRefDisplayEntries({
    value,
    data: params.data as Record<string, unknown> | undefined,
    field,
    fieldType,
    context,
  }).map((entry) => ({
    key: entry.key,
    label: entry.label,
    color: 'bg-neutral-50 text-neutral-900 border border-neutral-200',
    review: entry.isPending,
  }))
  if (chips.length === 0) return null

  return (
    <ul className="flex h-full flex-row items-center gap-1">
      {chips.map(({ key, label, color, review }) => (
        <li key={key} className="">
          <span
            className={cx(color, 'rounded-md px-2 py-0.5 leading-normal', {
              [pendingRefDisplayClassName]: review,
            })}
            title={review ? t('pending-option-tooltip') : undefined}
            aria-label={`${label}${
              review ? ` (${t('pending-option-tooltip-title')})` : ''
            }`}
          >
            {label}
          </span>
        </li>
      ))}
    </ul>
  )
}
